import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { isEmpty, kebabCase, keyBy, omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../../../../common/intl';
import { ChangeRequestStatus } from '../../../../../state/ducks/changeRequest/types';
import { companyActions } from '../../../../../state/ducks/company';
import { GeneralSettings } from '../../../../../state/ducks/company/types';
import { documentTypeActions, documentTypeSelectors } from '../../../../../state/ducks/documentRevisions/documentType';
import { DocumentType } from '../../../../../state/ducks/documentRevisions/documentType/types';
import { DocumentRevisionStatus } from '../../../../../state/ducks/documentRevisions/types';
import Loader from '../../../../components/common/kendo/Loader';
import {
  Mode,
  MODE_FIELD,
} from '../../../../components/KendoDataGrid/constants';
import { toastError } from '../../../../components/notifications';
import { FBRequiredApprover } from '../../../../form.builder';
import useActionCreator from '../../../../hooks/useActionCreator';
import useAsync from '../../../../hooks/useAsync';
import useDialog from '../../../../hooks/useDialog';
import ApprovalConfigurationUsageDialog from '../../components/configurationDialog/ApprovalConfigurationUsageDialog';
import ApproverConfigurationDialog from '../../components/configurationDialog/ApproverConfigurationDialog';
import StageFieldMappingDialog from '../../components/configurationDialog/StageFieldMappingDialog';
import SettingsPanel from '../../components/SettingsPanel';
import SettingsTable from '../../components/SettingsTable';
import { ApprovalConfigurationStage, SettingsPanelProps, toSortedOptions } from '../../types';
import { buildCoveredRoles, enrichConfiguration } from './helpers';
import { buildSchema } from './schema';
import {
  ApprovalConfiguration,
  ApprovalConfigurationUsage,
  EDIT_FIELDS,
  EditableApprovalConfiguration,
  OptionType,
  SelectedUsageEntry,
} from './types';

const ApprovalConfigurationPanel: React.FC<SettingsPanelProps> = (props) => {
  const { values: formValues } = useFormikContext<GeneralSettings>();

  const [approvalConfigurations, setApprovalConfigurations] = React.useState<ApprovalConfiguration[]>([]);
  const [editingId, setEditingId] = useState<string>();

  const [configurationStages, setConfigurationStages] = useState<ApprovalConfigurationStage[]>([]);

  const [configurationUsage, setConfigurationUsage] = useState<ApprovalConfigurationUsage>({});

  const [isStagesUpdated, setIsStagesUpdated] = useState<boolean>(false);
  const usageDialog = useDialog();

  const [transientUpdatedConfiguration, setTransientUpdatedConfiguration] = useState<ApprovalConfiguration>();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Document Types
  const allDocumentTypes: DocumentType[] = useSelector(documentTypeSelectors.getDocumentTypeList);
  const activeDocumentTypes = allDocumentTypes.filter(documentType => documentType.active);
  const documentTypeOptions = toSortedOptions(activeDocumentTypes,
    (documentType) => documentType.id,
    (documentType) => documentType.documentTypeName);
  const documentTypeOptionsMap = { ...(keyBy(documentTypeOptions, (group) => group.id)) };

  useEffect(() => {
    const enrichedApprovalConfigurations = enrichConfiguration(formValues?.approvalConfigurations ?? []);
    setApprovalConfigurations(enrichedApprovalConfigurations);
  }, [formValues?.approvalConfigurations, allDocumentTypes]);

  const configureDialog = useDialog();
  const configureFieldMappingDialog = useDialog();

  const discardConfiguration = () => {
    setEditingId(undefined);
    resetForm({});
  };

  const updateConfigurationAction = useActionCreator(companyActions.updateApprovalConfiguration);
  const copyConfigurationAction = useActionCreator(companyActions.copyApprovalConfiguration);
  const deleteConfigurationAction = useActionCreator(companyActions.deleteApprovalConfiguration);
  const async = useAsync({
    onError: (error) => {
      toastError(error);
    },
    onSuccess: () => {
      setIsLoading(true);
      discardConfiguration();
      fetchAllConfigurations();
    },
  });

  // Fetch details for where a configuration is used.
  const fetchConfigurationUsageAction = useActionCreator(companyActions.fetchConfigurationUsage);
  const fetchConfigurationUsageAsync = useAsync({
    onError: (error) => {
      toastError(error);
    },
    onSuccess: (data?: ApprovalConfigurationUsage) => {
      const usedInChangeRequests = (data?.changeRequestDetails ?? []);
      const usedInDocuments = (data?.docRevDetails ?? []);

      setConfigurationUsage(data as ApprovalConfigurationUsage);
      if (usedInChangeRequests.length || usedInDocuments.length) {
        usageDialog.open();
      } else {
        // If nothing to update, then update the config
        handleUpdateExistingEntities([]);
      }
    },
  });
  function fetchConfigurationUsage (configurationId: string) {
    fetchConfigurationUsageAsync.start(
      fetchConfigurationUsageAction,
      configurationId,
      { arStatus: ChangeRequestStatus.Draft, docRevStatus: DocumentRevisionStatus.Draft },
      fetchConfigurationUsageAsync,
    );
  }

  const handleUpdateExistingEntities = (selectedEntries: SelectedUsageEntry[]) => {
    async.start(
      updateConfigurationAction,
      { ...transientUpdatedConfiguration, entityIdsToUpdate: selectedEntries },
      async,
    );
    usageDialog.close();
  };

  const updateConfiguration = (updatedConfiguration: ApprovalConfiguration) => {
    if (updatedConfiguration.id && isStagesUpdated) {
      setTransientUpdatedConfiguration(updatedConfiguration);
      fetchConfigurationUsage(updatedConfiguration.id);
      return;
    }

    async.start(
      updateConfigurationAction,
      updatedConfiguration,
      async,
    );
  };

  const deleteConfiguration = (configurationId: string) => {
    async.start(
      deleteConfigurationAction,
      configurationId,
      async,
    );
  };

  const handleCopyConfiguration = (approvalConfiguration: EditableApprovalConfiguration) => {
    async.start(
      copyConfigurationAction,
      approvalConfiguration.id,
      async,
    );
  };

  const fetchAllConfigurationsAction = useActionCreator(companyActions.fetchAllApprovalConfigurations);
  const fetchApprovalConfigurationAsync = useAsync({
    onError: (error) => {
      toastError(error);
    },
    onSuccess: (data?: ApprovalConfiguration[]) => {
      const enrichedApprovalConfigurations = enrichConfiguration(data ?? []);
      setApprovalConfigurations(enrichedApprovalConfigurations);
      setIsLoading(false);
    },
  });
  const fetchAllConfigurations = () => {
    fetchApprovalConfigurationAsync.start(
      fetchAllConfigurationsAction,
      { isActiveOnly: false },
      fetchApprovalConfigurationAsync,
    );
  };

  const fetchRequiredApprovalsAction = useActionCreator(documentTypeActions.fetchRequiredApprovals);
  const fetchRequiredApprovalsAsync = useAsync({
    onError: () => {
      toastError('Error fetching required roles for selected Document Types');
    },
    onSuccess: (data?: FBRequiredApprover[] | undefined) => {
      const groupsData = (data ?? []).map(group => ({
        id: group.id,
        text: group.name,
        joinedEmployeeIds: group.joinedEmployees.map(joinedEmployee => joinedEmployee.id),
      }));
      const coveredRoles = buildCoveredRoles(values.approvalStages, values.roleCoverage);
      setValues({
        ...values,
        roleCoverage: { coveredRoles, requiredRoles: groupsData },
      });
    },
  });
  const handleDocumentTypeUpdated = (docTypesSelected: OptionType[]) => {
    const documentTypeIdsSelected = docTypesSelected.map(value => value.id);
    if (documentTypeIdsSelected.length) {
      setValues({
        ...values,
        documentTypeIds: documentTypeIdsSelected,
        documentTypeIds_edit: docTypesSelected,
      });
      fetchRequiredApprovalsAsync.start(
        fetchRequiredApprovalsAction,
        { ids: documentTypeIdsSelected },
        fetchRequiredApprovalsAsync,
      );
    } else {
      setValues({
        ...values,
        documentTypeIds: documentTypeIdsSelected,
        documentTypeIds_edit: docTypesSelected,
        roleCoverage: { coveredRoles: [], requiredRoles: [] },
      });
    }
  };

  const formik = useFormik<EditableApprovalConfiguration>({
    initialValues: {} as EditableApprovalConfiguration,
    validate: (values: Partial<EditableApprovalConfiguration>) => {
      const error: Record<string, string> = {};
      if (isEmpty(values.name)) {
        error.text = 'validators.required';
      }
      return error;
    },
    onSubmit: (values: Partial<EditableApprovalConfiguration>) => {
      const isAddition = values[MODE_FIELD] === Mode.add;
      const updatedValues = {
        ...omit(values, [MODE_FIELD, ...EDIT_FIELDS]),
        documentTypeIds: (values.documentTypeIds_edit ?? []).map(docTypeOption => docTypeOption.id),
      };
      const existingConfigurations = approvalConfigurations?.filter(configuration => configuration.name === values.name);
      if (isAddition && existingConfigurations?.length) {
        toastError(translate('administration.general.settings.creation.configuration.duplicate.error.msg'));
        return;
      }

      updateConfiguration(updatedValues as ApprovalConfiguration);
    },
  });
  const { setValues, submitForm, values, resetForm } = formik;

  const createEmptyConfiguration = (): EditableApprovalConfiguration => ({
    isActive: true,
    name: '',
    description: '',
    approvalStages: [],
    documentTypeIds: [],
    documentTypeIds_edit: [],
    formRevId: '',
    formDocumentId: '',
    roleCoverage: { coveredRoles: [], requiredRoles: [] },
    [MODE_FIELD]: Mode.add,
    usedCount: 0,
  });

  const addNewConfiguration = () => {
    const newConfiguration = createEmptyConfiguration();
    setEditingId(newConfiguration.id);
    setValues(newConfiguration);
  };

  const setEditedApprovalConfiguration = (item: EditableApprovalConfiguration) => {
    // If there is something already being edited, do nothing
    if (editingId) {
      return;
    }
    setEditingId(item.id);
    setValues(item);
  };

  const changeConfigurationActiveState = (configuration: EditableApprovalConfiguration, state: boolean) => {
    const editedConfiguration = approvalConfigurations?.find(it => it.id === configuration.id);
    if (editedConfiguration) {
      updateConfiguration({ ...editedConfiguration, isActive: state });
    }
  };

  const handleDeleteConfiguration = (dataItem: EditableApprovalConfiguration) => {
    if (dataItem.id) {
      if (dataItem.usedCount === 0) {
        deleteConfiguration(dataItem.id);
      } else {
        toastError(translate('administration.general.settings.approval.configuration.delete.err.msg', { usedCount: dataItem.usedCount }));
      }
    }
  };

  const handleApprovalStagesUpdated = (approvalStages?: ApprovalConfigurationStage[]) => {
    if (!approvalStages) {
      return;
    }

    setValues({
      ...values,
      approvalStages,
      roleCoverage: { ...values.roleCoverage, coveredRoles: buildCoveredRoles(approvalStages, values.roleCoverage) },
    });
    setIsStagesUpdated(true);
    configureDialog.close();
    setConfigurationStages([]);
  };

  const handleMappingUpdate = (
    approvalStages: ApprovalConfigurationStage[], selectedDocumentTypeId: string | undefined, formDocumentId: string | undefined,
  ) => {
    const updatedValues = {
      ...values,
      approvalStages,
    };
    if (selectedDocumentTypeId) {
      updatedValues.documentTypeIds = [selectedDocumentTypeId];
      updatedValues.documentTypeIds_edit = [documentTypeOptionsMap[selectedDocumentTypeId]];
    }
    if (formDocumentId) {
      updatedValues.formDocumentId = formDocumentId;
    }
    setValues(updatedValues);
    setIsStagesUpdated(true);
    configureFieldMappingDialog.close();
  };

  // Need to build the documentTypes here and also the summary.
  const mappedConfigurations = approvalConfigurations.map(configuration => {
    if (configuration.id !== editingId) {
      return {
        ...configuration,
        [MODE_FIELD]: Mode.show,
      };
    }

    return {
      ...configuration,
      ...values,
      [MODE_FIELD]: Mode.edit,
    };
  });

  const configurationsList = (
    [Mode.edit, Mode.add].includes(values[MODE_FIELD]) ? [...mappedConfigurations, values] : mappedConfigurations).map((configuration) => {
    const documentTypesWithText = configuration.documentTypeIds?.map((docTypeId: string) => (documentTypeOptionsMap[docTypeId]))
      .filter((role): role is OptionType => role !== undefined);
    return {
      ...configuration,
      documentTypeIds: documentTypesWithText,
      documentTypeIds_edit: documentTypesWithText,
    };
  });

  const dataCyGenerator = (approvalConfiguration: EditableApprovalConfiguration) => ({
    'data-cy': `row-${kebabCase(approvalConfiguration.name)}`,
  });

  const handleConfigureClick = (dataItem: ApprovalConfiguration) => {
    setConfigurationStages(dataItem.approvalStages ?? []);
    configureDialog.open();
  };

  const handleConfigureFieldMapping = (dataItem: ApprovalConfiguration) => {
    if (!dataItem.approvalStages.length) {
      toastError(translate('approval.configuration.stage.mapping.approval.stages.missing.err'));
    }
    configureFieldMappingDialog.open();
  };

  const isDeleteDisabled = (item: EditableApprovalConfiguration) => item.usedCount > 0;

  const schema = buildSchema({
    isActive: true,
    onEdit: setEditedApprovalConfiguration,
    onDiscard: discardConfiguration,
    onDelete: handleDeleteConfiguration,
    disableDelete: isDeleteDisabled,
    onActiveStateChange: changeConfigurationActiveState,
    onConfigureClick: handleConfigureClick,
    onConfigureFieldMapping: handleConfigureFieldMapping,
    onDocumentTypeUpdated: handleDocumentTypeUpdated,
    documentTypeList: documentTypeOptions,
    onConfirm: submitForm,
    onCopy: handleCopyConfiguration,
  });

  return (
    <SettingsPanel
      {...props}
      title={translate(props.title)}
      onAddNew={addNewConfiguration}
    >
      <FormikProvider value={formik}>
        <SettingsTable
          data={configurationsList as unknown as EditableApprovalConfiguration[]}
          isEditing={editingId !== undefined}
          schema={schema}
          addButtonLabel={translate('common.add.new')}
          onAdd={addNewConfiguration}
          getRowProperties={dataCyGenerator}
          isFilterable
        />
        {configureDialog.isOpen && <ApproverConfigurationDialog
          onApprovalStagesUpdated={handleApprovalStagesUpdated}
          isSaveAndAssignVisible={false}
          isWithinSettings={true}
          currentApprovalStages={configurationStages}
          configurationId={editingId}
          { ...{
            configureDialog,
          }}
        />}
        {usageDialog.isOpen && <ApprovalConfigurationUsageDialog
          usageDialog={usageDialog}
          usage={configurationUsage}
          onConfirm={handleUpdateExistingEntities}
        /> }
        {
          configureFieldMappingDialog.isOpen
            && <StageFieldMappingDialog
              configData={values}
              mappingDialog={configureFieldMappingDialog}
              handleMappingUpdate={handleMappingUpdate}
            />
        }
        {isLoading && <Loader />}
      </FormikProvider>
    </SettingsPanel>
  );
};
export default ApprovalConfigurationPanel;
